import { apiClient } from './api';
import {
  PromptEvolutionSession,
  PromptEvolutionIteration,
  PromptEvolutionResponse,
  GeneratedPrompt,
  getAgentById,
} from '../models/promptEvolution';

const SESSION_STORAGE_KEY = 'promptEvolutionSessions';

/**
 * Service for Prompt Evolution using the dedicated /api/agent/prompt/* endpoints
 */
export class PromptEvolutionAgentService {
  // Session Management - Local Storage
  static createSession(title: string, initialPrompt: string, description?: string): PromptEvolutionSession {
    const session: PromptEvolutionSession = {
      id: `prompt-evo-${Date.now()}`,
      title,
      description,
      initialPrompt,
      currentPrompt: initialPrompt,
      iterations: [],
      metadata: {
        totalIterations: 0,
        agentsUsed: [],
        exportHistory: [],
        tags: [],
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.saveSession(session);
    return session;
  }

  static saveSession(session: PromptEvolutionSession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);

    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }

    session.updatedAt = new Date().toISOString();
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
  }

  static getSession(sessionId: string): PromptEvolutionSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.id === sessionId) || null;
  }

  static getAllSessions(): PromptEvolutionSession[] {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static deleteSession(sessionId: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filtered));
  }

  /**
   * Start a new prompt evolution session on the backend
   */
  static async startBackendSession(
    startingPrompt: string,
    iterations: number = 3,
    maxTokensPerIteration: number = 2048
  ): Promise<{ sessionId: string; promptIterations: string[] }> {
    const response = await apiClient.post('/api/agent/prompt/start', {
      startingPrompt,
      iterations,
      maxTokensPerIteration,
    });
    return response.data;
  }

  /**
   * Iterate on an existing prompt evolution session
   */
  static async iteratePrompt(
    backendSessionId: string,
    userMessage: string,
    agentType?: string,
    temperature?: number,
    maxResponseTokens?: number
  ): Promise<{
    sessionId: string;
    improvedPrompt: string;
    iterationCount: number;
    allIterations: string[];
  }> {
    const response = await apiClient.post(`/api/agent/prompt/${backendSessionId}/iterate`, {
      userMessage,
      agentType,
      temperature,
      maxResponseTokens,
    });
    return response.data;
  }

  /**
   * Evolve a prompt using the Prompt Evolution Agent API
   */
  static async evolvePrompt(
    sessionId: string,
    agentType: string,
    customInstructions?: string,
    temperature: number = 0.7
  ): Promise<PromptEvolutionResponse> {
    const session = this.getSession(sessionId);
    if (!session) {
      throw new Error('Session not found');
    }

    const agent = getAgentById(agentType);
    if (!agent) {
      throw new Error('Agent not found');
    }

    try {
      const currentPrompt = session.currentPrompt;

      // Check if we have a backend session ID stored
      let backendSessionId = session.metadata.backendSessionId;

      if (!backendSessionId) {
        // First evolution - start a new backend session
        const startResponse = await this.startBackendSession(currentPrompt, 1, 2048);
        backendSessionId = startResponse.sessionId;

        // Store backend session ID in metadata
        session.metadata.backendSessionId = backendSessionId;
        this.saveSession(session);
      }

      // Build the user message for iteration
      const userMessage = customInstructions
        ? `Using the ${agent.name} agent perspective: ${customInstructions}\n\nCurrent prompt: ${currentPrompt}`
        : `Using the ${agent.name} agent perspective, evolve this prompt with focus on: ${agent.specialties.join(', ')}\n\nCurrent prompt: ${currentPrompt}`;

      // Call iterate endpoint
      const iterateResponse = await this.iteratePrompt(
        backendSessionId,
        userMessage,
        agentType,
        temperature,
        2048
      );

      // Generate variants from the improved prompt
      const generatedPrompts: GeneratedPrompt[] = [
        {
          id: `variant-1`,
          prompt: iterateResponse.improvedPrompt,
          explanation: `Enhanced by ${agent.name}`,
        },
        // Generate two additional variants based on agent specialties
        {
          id: `variant-2`,
          prompt: this.generateVariant(iterateResponse.improvedPrompt, agent.specialties[0] || 'detail'),
          explanation: `Focused on ${agent.specialties[0] || 'enhanced details'}`,
        },
        {
          id: `variant-3`,
          prompt: this.generateVariant(iterateResponse.improvedPrompt, agent.specialties[1] || 'style'),
          explanation: `Emphasizing ${agent.specialties[1] || 'stylistic elements'}`,
        },
      ];

      // Create iteration record
      const iteration: PromptEvolutionIteration = {
        id: `iteration-${Date.now()}`,
        iterationNumber: session.iterations.length + 1,
        agentType,
        agentName: agent.name,
        inputPrompt: currentPrompt,
        generatedPrompts,
        customInstructions,
        timestamp: new Date().toISOString(),
        agentResponse: {
          sessionId: iterateResponse.sessionId,
          improvedPrompt: iterateResponse.improvedPrompt,
          iterationNumber: iterateResponse.iterationCount,
          allIterations: iterateResponse.allIterations,
          agentType,
          success: true,
        },
      };

      // Update session
      session.iterations.push(iteration);
      session.metadata.totalIterations++;
      if (!session.metadata.agentsUsed.includes(agentType)) {
        session.metadata.agentsUsed.push(agentType);
      }
      this.saveSession(session);

      return {
        success: true,
        iterationId: iteration.id,
        generatedPrompts,
        agentInsights: agent.description,
      };

    } catch (error: any) {
      console.error('Evolution error:', error);

      // Return error response with required fields
      return {
        success: false,
        iterationId: '',
        generatedPrompts: [],
        error: error.response?.data?.detail || error.message || 'Failed to evolve prompt',
      };
    }
  }

  /**
   * Generate a variant of a prompt based on a focus area
   */
  private static generateVariant(basePrompt: string, focusArea: string): string {
    const variantStrategies: Record<string, (p: string) => string> = {
      'detail': (p) => `${p}, with intricate details, comprehensive elements`,
      'detailed': (p) => `${p}, highly detailed, rich in specifics`,
      'atmosphere': (p) => `${p}, atmospheric depth, immersive mood`,
      'visual': (p) => `${p}, striking visual composition, aesthetic excellence`,
      'narrative': (p) => `${p}, compelling narrative elements, storytelling depth`,
      'emotion': (p) => `${p}, emotionally resonant, expressive qualities`,
      'style': (p) => `${p}, distinctive stylistic choices, artistic flair`,
      'technical': (p) => `${p}, technical precision, professional execution`,
      'creative': (p) => `${p}, creative interpretation, innovative approach`,
      'cinematic': (p) => `${p}, cinematic quality, dramatic framing`,
    };

    const focusLower = focusArea.toLowerCase();
    const strategy = variantStrategies[focusLower] || ((p) => `${p}, enhanced with ${focusArea} focus`);

    return strategy(basePrompt);
  }

  /**
   * Select a prompt variant and update current prompt
   */
  static selectPrompt(sessionId: string, iterationId: string, promptIndex: number): void {
    const session = this.getSession(sessionId);
    if (!session) return;

    const iteration = session.iterations.find(i => i.id === iterationId);
    if (!iteration || !iteration.generatedPrompts[promptIndex]) return;

    iteration.selectedPromptIndex = promptIndex;
    session.currentPrompt = iteration.generatedPrompts[promptIndex].prompt;
    this.saveSession(session);
  }

  /**
   * Export session
   */
  static exportSession(session: PromptEvolutionSession, format: 'json' | 'markdown' | 'text' = 'json'): void {
    let content: string;
    let filename: string;
    let mimeType: string;

    switch (format) {
      case 'markdown':
        content = this.generateMarkdownExport(session);
        filename = `prompt-evolution-${session.id}.md`;
        mimeType = 'text/markdown';
        break;
      case 'text':
        content = this.generateTextExport(session);
        filename = `prompt-evolution-${session.id}.txt`;
        mimeType = 'text/plain';
        break;
      default:
        content = JSON.stringify(session, null, 2);
        filename = `prompt-evolution-${session.id}.json`;
        mimeType = 'application/json';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);

    // Update export history
    session.metadata.exportHistory.push({
      exportedAt: new Date().toISOString(),
      format,
      includesImages: false,
    });
    this.saveSession(session);
  }

  private static generateMarkdownExport(session: PromptEvolutionSession): string {
    let content = `# Prompt Evolution Session: ${session.title}\n\n`;
    content += `**Created:** ${new Date(session.createdAt).toLocaleString()}\n`;
    content += `**Description:** ${session.description || 'N/A'}\n\n`;
    content += `## Initial Prompt\n${session.initialPrompt}\n\n`;
    content += `## Current Prompt\n${session.currentPrompt}\n\n`;
    content += `## Evolution History\n\n`;

    session.iterations.forEach((iteration, index) => {
      content += `### Iteration ${index + 1} - ${iteration.agentName}\n`;
      content += `**Input:** ${iteration.inputPrompt}\n\n`;
      if (iteration.customInstructions) {
        content += `**Instructions:** ${iteration.customInstructions}\n\n`;
      }
      content += `**Generated Variants:**\n\n`;

      iteration.generatedPrompts.forEach((prompt, i) => {
        const selected = iteration.selectedPromptIndex === i ? ' ✓' : '';
        content += `${i + 1}. ${prompt.prompt}${selected}\n`;
        if (prompt.explanation) {
          content += `   *${prompt.explanation}*\n`;
        }
        content += '\n';
      });
    });

    return content;
  }

  private static generateTextExport(session: PromptEvolutionSession): string {
    let content = `PROMPT EVOLUTION SESSION: ${session.title}\n`;
    content += `${'='.repeat(50)}\n\n`;
    content += `Initial: ${session.initialPrompt}\n\n`;
    content += `Current: ${session.currentPrompt}\n\n`;
    content += `Iterations:\n`;

    session.iterations.forEach((iteration, index) => {
      content += `\n${index + 1}. ${iteration.agentName}\n`;
      iteration.generatedPrompts.forEach((prompt, i) => {
        const selected = iteration.selectedPromptIndex === i ? ' [SELECTED]' : '';
        content += `   - ${prompt.prompt}${selected}\n`;
      });
    });

    return content;
  }
}

export default PromptEvolutionAgentService;
